import React, { Component, type ReactNode, Fragment } from 'react'
import styled from '@emotion/styled'
import { Button, SVGIcon, LoadingBox, colors } from 'ic-snacks'
import { FormattedMessage } from 'react-intl'
import { withFlipperContext } from 'legacy/common/context'
import SubNav from 'legacy/components/Nav/SubNav'
import SubNavItem, { SubnavDescription } from 'legacy/components/Nav/SubNavItem'
import { NavLinkText } from 'legacy/components/Nav/NavTypography'
import withRetailerRouter from 'legacy/common/withRouter'
import { type RetailerRouteComponentProps } from 'legacy/components/RetailerRoute'
import { type FlipperContextValue } from 'legacy/contexts/flipper/FlipperContext'
import { withTrackEventOnMount } from 'utils/events/hocs'
import AppHeader from 'legacy/components/AppHeader'
import ProductsTable from 'legacy/routes/catalog/products/ProductsTable'
import NewProductModal from 'legacy/routes/catalog/NewProductModal'
import { type Department, type Aisle } from 'legacy/routes/catalog/types'
import { type Product } from 'legacy/routes/catalog/types/product.types'
import DepartmentsBreadcrumbs from 'legacy/routes/catalog/breadcrumbs/DepartmentsBreadcrumbs'
import EditAisleModal from './EditAisleModal'
import AisleHeader from './AisleHeader'

const AisleNavContainer = styled.div`
  border-right: 1px solid ${colors.GRAY_93};
  height: 100%;
`

const ProductsContainer = styled.div`
  flex: 1;
`

const Container = styled.div`
  display: flex;
  flex: 1;
  min-height: calc(100vh - 60px);
`

const AislesContainer = ({ children }: { children: ReactNode }) => <Aisles>{children}</Aisles>

const DragHandle = () => (
  <SVGIcon
    name="sort"
    data-testid="aisle-sort-drag-handle"
    style={{ cursor: 'all-scroll', marginLeft: '8px' }}
  />
)

const AislesListHeader = styled(SubNavItem)<{ aisleSortingEnabled: boolean }>`
  ${SubnavDescription} {
    padding-left: ${props => (props.aisleSortingEnabled ? '38px' : '20px')};
  }
`

const SortableAisleContainer = styled.div`
  display: flex;
  align-items: center;

  div[role='link'] {
    width: 100%;
  }

  ${SubnavDescription} {
    padding-left: 8px;
  }

  ${NavLinkText} {
    white-space: initial;
  }
`

const Aisles = styled.div`
  display: flex;
  flex-direction: column;
`

interface Params {
  aisle_id: string
  department_id: string
}

interface Query {
  term: string
  page: string
  addProducts: string
}

interface Props extends RetailerRouteComponentProps<Params, Query>, FlipperContextValue {
  department?: Department
  aisles?: Aisle[]
  productsPromise?: Promise<{ data?: Product[] }>
  onAisleDelete(aisleId: number): void
  onAislesOrderChange(aisleId: number, rankOffsetPosition: number): void
  onlyPrimaryProducts: boolean
  onToggleOnlyPrimaryProducts: Function
  getAisles: () => void
  useMesh?: Boolean
}

interface State {
  showNewAisleModal: boolean
  showEditAisleModal: boolean
}

class DepartmentView extends Component<Props, State> {
  state: State = {
    showNewAisleModal: false,
    showEditAisleModal: false,
  }

  notifyError() {
    // Noop for now
  }

  getPath(aisleId: number) {
    const { department } = this.props

    return this.props.url('aisle', {
      department_id: `${(department as Department).id}`,
      aisle_id: `${aisleId}`,
    })
  }

  onAislesSort = ({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }) => {
    const { aisles, onAislesOrderChange } = this.props

    if (!aisles) {
      return
    }

    const aisle = aisles[oldIndex]
    const rankOffsetPosition = aisles.findIndex(d => d === aisles[newIndex])

    onAislesOrderChange(aisle.id, rankOffsetPosition)
  }

  renderLoading() {
    return (
      <Container data-testid="department-view-container-loading">
        <LoadingBox shape="square" background="light" size={220} style={{ height: '100vm' }} />
        <LoadingBox
          shape="square"
          background="light"
          size={0}
          style={{ marginLeft: '5px', height: '60px', width: 'calc(100vw - 280px)' }}
        />
      </Container>
    )
  }

  render() {
    const {
      aisles,
      department,
      productsPromise,
      url,
      match,
      match: { params },
      onAisleDelete,
      query: { term, page, addProducts },
      flipper,
      onlyPrimaryProducts,
      onToggleOnlyPrimaryProducts,
      useMesh,
    } = this.props

    const { showNewAisleModal, showEditAisleModal } = this.state

    if (!aisles || !department) {
      return this.renderLoading()
    }

    const SortableAisle = ({ value: aisle }: { value: Aisle }) => (
      <SortableAisleContainer>
        {flipper.aisle_ordering && <DragHandle />}
        <SubNavItem
          elementAttributes={{
            style: {
              backgroundColor: colors.WHITE,
            },
          }}
          to={this.getPath(aisle.id)}
          q={{ page: 1 }}
          iconName={aisle.visible ? undefined : 'view'}
          testId={`sub-nav-item-aisle-${aisle.id}`}
          details={
            <FormattedMessage
              values={{ product_count: aisle.product_count }}
              id="catalog.aisle.countSummary"
            />
          }
        >
          {aisle.name}
        </SubNavItem>
      </SortableAisleContainer>
    )

    return (
      <Fragment>
        <AppHeader title={<DepartmentsBreadcrumbs />} />
        <Container data-testid="department-view-container">
          <AisleNavContainer>
            <SubNav>
              <Button
                onClick={() => this.setState({ showNewAisleModal: true })}
                snacksStyle="flat"
                style={{ height: '60px', width: '100%' }}
              >
                <SVGIcon name="plus" />
                <FormattedMessage id="aisles.create" />
              </Button>
              <AislesListHeader
                aisleSortingEnabled={Boolean(flipper.aisle_ordering)}
                exact
                to={url('aisles', { department_id: String(department.id) })}
                q={{ page: 1 }}
              >
                <FormattedMessage id="aisles.all" />
              </AislesListHeader>
              <AislesContainer>
                {aisles.map((aisle, index) => (
                  <SortableAisle key={aisle.id} value={aisle} />
                ))}
              </AislesContainer>
            </SubNav>
          </AisleNavContainer>
          <ProductsContainer>
            <AisleHeader
              match={match}
              onEditClick={() => this.setState({ showEditAisleModal: true })}
              onDelete={onAisleDelete}
              onlyPrimaryProducts={onlyPrimaryProducts}
              onToggleOnlyPrimaryProducts={onToggleOnlyPrimaryProducts}
              useMesh={useMesh}
            />
            <ProductsTable
              notifyError={this.notifyError}
              promise={productsPromise}
              key={term}
              page={parseInt(page || '1', 10) - 1}
              noDataText="No Products"
            />
            {addProducts === 'aisle' && params.aisle_id && (
              <NewProductModal aisleId={parseInt(params.aisle_id, 10)} />
            )}
          </ProductsContainer>

          {showNewAisleModal && (
            <EditAisleModal
              onClose={() => this.setState({ showNewAisleModal: false })}
              params={params}
              getAisles={this.props.getAisles}
            />
          )}

          {showEditAisleModal && (
            <EditAisleModal
              id={params.aisle_id}
              onClose={() => this.setState({ showEditAisleModal: false })}
              aisle={aisles.find(a => String(a.id) === params.aisle_id)}
              params={match.params}
              getAisles={this.props.getAisles}
            />
          )}
        </Container>
      </Fragment>
    )
  }
}

export default withTrackEventOnMount({
  id: 'catalog.departments.department.aisle.viewed',
  description: "Viewed an aisle [or the 'all-aisles' section] of a department",
})(withRetailerRouter(withFlipperContext(DepartmentView)))
